import React, { useState, useEffect, useCallback } from 'react';
import { useSearchFilters } from '../contexts/SearchContext';
import { defineMessages } from 'react-intl';
import { SRP_INLINE_SELECT_CLASSNAME } from '../utils/srp/srp-inline-select-classname';
import { useFormatMessage } from '../i18n-review/client/Msg';
import { NEW_TODAY_FIRST_SEEN_CODE } from '../utils/search-router/first-seen-threshold';

/**
 * Option labels are looked up dynamically below (`formatMessage({ id: option.labelKey, ... })`),
 * so `extract:i18n`'s static extractor can't see them at the call site — this block is the
 * extraction source that keeps every search.refinements.lastSeen.* id in en.json (mirrors the
 * same pattern in footer.messages.ts). Values must stay in sync with LAST_SEEN_OPTIONS below.
 */
const lastSeenMessages = defineMessages({
  all: {
    id: 'search.refinements.lastSeen.all',
    defaultMessage: 'All',
    description: 'Date-posted filter option: no time restriction',
  },
  past6Hours: {
    id: 'search.refinements.lastSeen.past6Hours',
    defaultMessage: 'Past 6 hours',
    description: 'Date-posted filter option: jobs posted in the last 6 hours',
  },
  past24Hours: {
    id: 'search.refinements.lastSeen.past24Hours',
    defaultMessage: 'Past 24 hours',
    description: 'Date-posted filter option: jobs posted in the last 24 hours',
  },
  past3Days: {
    id: 'search.refinements.lastSeen.past3Days',
    defaultMessage: 'Past 3 days',
    description: 'Date-posted filter option: jobs posted in the last 3 days',
  },
  past1Week: {
    id: 'search.refinements.lastSeen.past1Week',
    defaultMessage: 'Past 1 week',
    description: 'Date-posted filter option: jobs posted in the last week',
  },
  past2Weeks: {
    id: 'search.refinements.lastSeen.past2Weeks',
    defaultMessage: 'Past 2 weeks',
    description: 'Date-posted filter option: jobs posted in the last 2 weeks',
  },
  past1Month: {
    id: 'search.refinements.lastSeen.past1Month',
    defaultMessage: 'Past 1 month',
    description: 'Date-posted filter option: jobs posted in the last month',
  },
});

export type LastSeenOption = 'all' | '6h' | '24h' | '3d' | '1w' | '2w' | '1m';

interface LastSeenOptionConfig {
  value: LastSeenOption;
  labelKey: string;
  defaultLabel: string;
  hours: number;
}

export const LAST_SEEN_OPTIONS: LastSeenOptionConfig[] = [
  { value: 'all', labelKey: 'search.refinements.lastSeen.all', defaultLabel: 'All', hours: 0 },
  { value: '6h', labelKey: 'search.refinements.lastSeen.past6Hours', defaultLabel: 'Past 6 hours', hours: 6 },
  { value: '24h', labelKey: 'search.refinements.lastSeen.past24Hours', defaultLabel: 'Past 24 hours', hours: 24 },
  { value: '3d', labelKey: 'search.refinements.lastSeen.past3Days', defaultLabel: 'Past 3 days', hours: 72 },
  { value: '1w', labelKey: 'search.refinements.lastSeen.past1Week', defaultLabel: 'Past 1 week', hours: 168 },
  { value: '2w', labelKey: 'search.refinements.lastSeen.past2Weeks', defaultLabel: 'Past 2 weeks', hours: 336 },
  { value: '1m', labelKey: 'search.refinements.lastSeen.past1Month', defaultLabel: 'Past 1 month', hours: 720 },
];

/**
 * Pure version of the component's own timestamp->option matching (extracted, TEC navbar
 * redesign Phase 2 item 2.1/2.3): given a stored `firstSeenMin` threshold, finds which option
 * would have produced a threshold closest to it right now. Exported so the SRP tape's token
 * renderer (components/filters/TopFilters.tsx) can render the exact same "Past 7 days"-style
 * label the modal's own selector would show for the same stored value, instead of a second,
 * possibly-drifting copy of this matching logic.
 */
export function getLastSeenOptionFromTimestamp(timestamp: string | undefined, now = Date.now()): LastSeenOption {
  if (!timestamp || timestamp === 'all') return 'all';
  // Stable relative codes ("24h", "1w") are the option values themselves.
  if (LAST_SEEN_OPTIONS.some((option) => option.value === timestamp)) return timestamp as LastSeenOption;
  const num = Number(timestamp);
  if (isNaN(num)) return 'all';

  let bestMatch: LastSeenOption = 'all';
  let smallestDiff = Infinity;
  for (const option of LAST_SEEN_OPTIONS) {
    if (option.hours > 0) {
      const expectedThreshold = now - option.hours * 60 * 60 * 1000;
      const diff = Math.abs(num - expectedThreshold);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        bestMatch = option.value;
      }
    }
  }
  return bestMatch;
}

/** Display label for a stored `firstSeenMin` value, e.g. "Past 7 days" -> the tape's "Last 7
 * days". Returns null for 'all' (nothing to show; the caller shouldn't render a token). */
const newTodayLabel = defineMessages({
  newToday: {
    id: 'lastSeen.newToday',
    defaultMessage: 'New today',
    description:
      "Label of the posted-date filter token in the search page's filter row when the visitor came from the navbar's 'new today' figure (jobs first seen in today's crawl)",
  },
}).newToday;

export function resolveLastSeenLabel(rawValue: string | undefined, formatMessage: (descriptor: { id: string; defaultMessage: string }) => string): string | null {
  // The navbar's "new today" link stores its own code, not a timestamp; without a label the
  // filter row rendered nothing while the filter still counted as applied, so the SRP lost both
  // its second navbar row and its mobile top bar.
  if (rawValue === NEW_TODAY_FIRST_SEEN_CODE) return formatMessage(newTodayLabel);
  const option = getLastSeenOptionFromTimestamp(rawValue);
  if (option === 'all') return null;
  const config = LAST_SEEN_OPTIONS.find((o) => o.value === option);
  if (!config) return null;
  return formatMessage({ id: config.labelKey, defaultMessage: config.defaultLabel });
}

export interface LastSeenSelection {
  selectedOption: LastSeenOption;
  handleChange: (option: LastSeenOption) => void;
}

/**
 * The stateful half of LastSeenSelector, extracted (TEC navbar redesign item 6) so the Advanced
 * Filters modal's Posted section can drive a styled option list (the same look as its Sort
 * section) with the exact same selection/`firstSeenMin`-writing behaviour as the native `<select>`
 * below, instead of duplicating LAST_SEEN_OPTIONS or the stable-code storage logic. Every other
 * consumer of the plain `<select>` (TopFilters.tsx, SearchSidebar.tsx, ResultsPerPageSelector.tsx,
 * and the classic flag-off path in AllFiltersModal.tsx) is unaffected: `LastSeenSelector` below
 * still renders the same `<select>` it always has, just now built on top of this hook.
 */
export function useLastSeenSelection(): LastSeenSelection {
  const { filters, setFilters } = useSearchFilters();
  const filterObj = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};

  // Convert timestamp back to option value (extracted to the pure, exported
  // getLastSeenOptionFromTimestamp above, TEC navbar redesign Phase 2 item 2.1/2.3, so the SRP
  // tape's token renderer can match the same stored value to the same label).
  const getOptionFromTimestamp = useCallback((timestamp: string | undefined): LastSeenOption => {
    return getLastSeenOptionFromTimestamp(timestamp);
  }, []);

  const [selectedOption, setSelectedOption] = useState<LastSeenOption>(() => {
    const current = filterObj.firstSeenMin?.[0];
    if (current === 'all' || !current) return 'all';
    return getOptionFromTimestamp(current);
  });

  useEffect(() => {
    const current = filterObj.firstSeenMin?.[0];
    if (current === 'all' || !current) {
      setSelectedOption('all');
    } else {
      setSelectedOption(getOptionFromTimestamp(current));
    }
  }, [filterObj.firstSeenMin, getOptionFromTimestamp]);

  const handleChange = useCallback((option: LastSeenOption) => {
    setSelectedOption(option);

    const base = filterObj;
    const nextFilters: Record<string, string[]> = { ...base };

    if (option === 'all') {
      delete nextFilters.firstSeenMin;
    } else {
      const optionConfig = LAST_SEEN_OPTIONS.find(o => o.value === option);
      if (optionConfig && optionConfig.hours > 0) {
        // Stored as the stable relative code ("24h"), not an epoch timestamp, so the resulting
        // URL is shareable and identical across visits; it resolves to the rolling window at
        // search time (utils/search-router/first-seen-threshold.ts). Old timestamp URLs still work.
        nextFilters.firstSeenMin = [optionConfig.value];
      }
    }

    const hasAny = Object.keys(nextFilters).length > 0;
    setFilters(hasAny ? nextFilters : {});
  }, [filterObj, setFilters]);

  return { selectedOption, handleChange };
}

interface LastSeenSelectorProps {
  className?: string;
}

export const LastSeenSelector: React.FC<LastSeenSelectorProps> = ({ className = '' }) => {
  const formatMessage = useFormatMessage();
  const { selectedOption, handleChange } = useLastSeenSelection();

  return (
    <select
      className={`${SRP_INLINE_SELECT_CLASSNAME} ${className}`}
      value={selectedOption}
      onChange={(e) => handleChange(e.target.value as LastSeenOption)}
      aria-label={formatMessage({ id: 'search.refinements.lastSeen.aria', defaultMessage: 'Date posted', description: 'Accessibility label for posted-date time window filter' })}
    >
      {LAST_SEEN_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {formatMessage({ id: option.labelKey, defaultMessage: option.defaultLabel })}
        </option>
      ))}
    </select>
  );
};

